import { FormEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { RhyBusAlert } from '@rhybus/alert'
import { RhyBusButton } from '@rhybus/button'
import { RhyBusCard } from '@rhybus/card'
import { RhyBusExportToCSV } from '@rhybus/export-csv'
import { RhyBusFilters, RhyBusFiltersRef } from '@rhybus/filters'
import { RhyBusPageHeader } from '@rhybus/header'
import { RhyBusKPI } from '@rhybus/kpi'
import { RhyBusTableView } from '@rhybus/table-view'
import { RhyBusTabs } from '@rhybus/tabs'
import { RhyBusCopyTo } from '@rhybus/copy-to'
import { useGetMasterListQuery } from '../../store/master/hooks'
import {
    buildDefaultExportColumns, buildDynamicFilterConfig, buildCascadingFilterConfig, buildInitialFilters,
    filterRequirements, getAvailableColumnsByReference, getAvailableExportColumns, getListViewExportFileName, getRequirementKpiGroups, getRequirementTabCounts, getTabRequirements, getUserPreferences, saveUserPreferences, applyKpiFilter, hasSelectedRequirements,
    parseColumnData, validateTableColumnSelection
} from '../../services/listview'
import { FilterState, ListViewExportColumn, REQUIREMENT_SELECTION_VALIDATION_MESSAGE, EXPORT_SOURCE_LISTVIEW, TABLE_SOURCE_LISTVIEW, RequirementTableColumn, defaultSelectedExportColumns, RequirementItem, RequirementTabKey, statusColorMap, GRANT_TABLE_COLUMNS, grantSampleData, COPY_VALIDATION_MESSAGE } from '../../config/configuration'
import './list-view.css'
import { RhyBusInputText } from '@rhybus/form-controls'
import PageOverlay from '../../utils/controls/pageoverLay/pageoverLay'

const EMPTY_REQUIREMENTS: RequirementItem[] = []

export default function ListView() {
    const [isExpanddetail, setIsExpanddetail] = useState(false)
    const { data: items = EMPTY_REQUIREMENTS, isLoading } = useGetMasterListQuery(undefined, { refetchOnMountOrArgChange: true })
    const location = useLocation()
    const [activeTab, setActiveTab] = useState(() => {
        const state = location.state as { tab?: string } | null
        if (state?.tab === 'saved') return 1
        if (state?.tab === 'canceled') return 2
        return 0
    })
    const [searchText, setSearchText] = useState('')
    const [showSidebar, setShowSidebar] = useState(false)
    const [showAlert, setShowAlert] = useState<any>(false)
    const [dynamicFilterConfig, setDynamicFilterConfig] = useState<any[]>([])
    const [selectedExportColumns, setSelectedExportColumns] = useState<ListViewExportColumn[]>(defaultSelectedExportColumns)
    const [availableExportColumns, setAvailableExportColumns] = useState<ListViewExportColumn[]>(getAvailableExportColumns())
    const [userPreferenceId, setUserPreferenceId] = useState<number | undefined>(undefined)
    const [tablePreferenceId, setTablePreferenceId] = useState<number | undefined>(undefined)
    const [tableKey, setTableKey] = useState(0)
    const [tableInitPhase, setTableInitPhase] = useState<'init' | 'ready'>('ready')
    const isSaveClickRef = useRef(false)
    const [kpiFilteredItems, setKpiFilteredItems] = useState<RequirementItem[] | null>(null)
    const [filters, setFilters] = useState<FilterState>({})
    const [filtersKey, setFiltersKey] = useState(0)
    const [checkedItems, setCheckedItems] = useState<RequirementItem[]>([])
    const [searchInputKey, setSearchInputKey] = useState(0)
    const [columnErrorMsg, setColumnErrorMsg] = useState('')
    const expanddetail = () => setIsExpanddetail(!isExpanddetail)
    const [userId] = useState<string>(() => {
        try {
            return (window as any)._spPageContextInfo?.userId?.toString() || ''
        } catch {
            return ''
        }
    })
    const filtersInitialized = useRef(false)

    useEffect(() => {
        if (!items.length || filtersInitialized.current) return
        filtersInitialized.current = true
        const newConfig = buildDynamicFilterConfig(items)
        setDynamicFilterConfig(newConfig)
        const raw = sessionStorage.getItem('homeFilters')
        const navState = location.state as any
        const incomingFilters = navState?.initialFilters
        const chartFilter = navState?.chartFilter
        if (incomingFilters && typeof incomingFilters === 'object' && Object.keys(incomingFilters).length > 0) {
            const initial = buildInitialFilters(newConfig)
            const merged = { ...initial }
            Object.entries(incomingFilters as Record<string, { order: number; value: { label: string; value: string }[] }>).forEach(([key, val]) => {
                if (key in merged && Array.isArray(val?.value) && val.value.length > 0) {
                    merged[key] = { order: val.order ?? merged[key]?.order ?? 0, value: val.value }
                }
            })
            setFilters(merged)
        } else if (chartFilter?.key && Array.isArray(chartFilter.value) && chartFilter.value.length > 0) {
            const initial = buildInitialFilters(newConfig)
            setFilters({ ...initial, [chartFilter.key]: { order: chartFilter.order ?? 0, value: chartFilter.value } })
        } else if (raw) {
            sessionStorage.removeItem('homeFilters')
            try { setFilters(JSON.parse(raw)) } catch { setFilters(buildInitialFilters(newConfig)) }
        } else {
            setFilters(buildInitialFilters(newConfig))
        }
        setFiltersKey(prev => prev + 1)
    }, [items])

    useEffect(() => {
        const initializeExportColumns = async () => {
            try {
                const configuredColumns = await getAvailableColumnsByReference(EXPORT_SOURCE_LISTVIEW)
                const fallbackColumns = buildDefaultExportColumns()
                const baseColumns = configuredColumns.length > 0 ? configuredColumns : fallbackColumns
                const savedPreference = await getUserPreferences(userId, EXPORT_SOURCE_LISTVIEW)
                const preferredColumns = parseColumnData(savedPreference?.SelectedColumns)
                const finalColumns = preferredColumns.length > 0 ? preferredColumns : baseColumns
                const selectedColumns = finalColumns.filter(column => column.IsSelected !== false)

                setSelectedExportColumns(selectedColumns)
                setAvailableExportColumns(finalColumns.filter(column => column.IsSelected === false))
                setUserPreferenceId(savedPreference?.ID)
            } catch (error) {
                console.error('Failed to initialize export columns:', error)
                const fallbackColumns = buildDefaultExportColumns()
                setSelectedExportColumns(fallbackColumns.filter(column => column.IsSelected !== false))
                setAvailableExportColumns(fallbackColumns.filter(column => column.IsSelected === false))
            }
        }

        initializeExportColumns()
    }, [userId])

    useEffect(() => {
        const initializeTableColumns = async () => {
            try {
                const savedPreference = await getUserPreferences(userId, TABLE_SOURCE_LISTVIEW)
                setTablePreferenceId(savedPreference?.ID)
                setTableInitPhase('init')
                setTableKey(prev => prev + 1)
            } catch (error) {
                console.error('Failed to initialize table columns:', error)
                setTableInitPhase('ready')
            }
        }

        initializeTableColumns()
    }, [userId])

    useEffect(() => {
        if (tableInitPhase === 'init') {
            setTableInitPhase('ready')
        }
    }, [tableInitPhase])

    const navigate = useNavigate()
    const RhyBusFiltersRef = useRef<RhyBusFiltersRef>(null)
    const exportCsvContainerRef = useRef<any>(null)
    const filteredRequirements = useMemo(() => filterRequirements(items, filters, searchText), [items, filters, searchText])
    const allTabItems = useMemo(() => items.filter(item => item.tabKey === 'all'), [items])
    const cascadingFilterConfig = useMemo(() => buildDynamicFilterConfig(allTabItems), [allTabItems])

    const activeTabKey = useMemo<RequirementTabKey>(() => {
        if (activeTab === 1) return 'saved'
        if (activeTab === 2) return 'canceled'
        return 'all'
    }, [activeTab])

    // Use KPI filtered items if available, otherwise use regular filtered items
    const displayItems = kpiFilteredItems !== null ? kpiFilteredItems : filteredRequirements

    const exportData = useMemo(() => getTabRequirements(displayItems, activeTabKey), [activeTabKey, displayItems])
    const copiedData = useMemo(() => checkedItems, [checkedItems])
    const tabCounts = useMemo(() => getRequirementTabCounts(items), [items])
    const kpiGroups = useMemo(() => getRequirementKpiGroups(filteredRequirements), [filteredRequirements])

    const handleSelectionChange = useCallback((selectedItems: RequirementItem[]) => {
        setCheckedItems(selectedItems ?? [])
    }, [])

    useEffect(() => {
        setCheckedItems(current => current.length > 0 ? [] : current)
    }, [exportData])

    useEffect(() => {
        document.querySelectorAll('.lv-column-error').forEach(el => el.remove())
        if (!columnErrorMsg) return
        const configPopover = Array.from(document.querySelectorAll('.rb-custom-popover')).find((el: any) =>
            el.querySelector('.popover-header')?.textContent?.includes('Configure Columns')
        )
        if (!configPopover) return
        const footer = configPopover.querySelector('[data-popoverfooter]')
        if (!footer) return
        const errorEl = document.createElement('div')
        errorEl.className = 'lv-column-error'
        errorEl.textContent = columnErrorMsg
        configPopover.insertBefore(errorEl, footer)
    }, [columnErrorMsg])

    const handleSaveVisibleColumns = useCallback(async (visibleColumns: RequirementTableColumn[]) => {
        if (!isSaveClickRef.current) {
            // Fired by checkbox onChange — do not update the table or persist
            return
        }
        isSaveClickRef.current = false

        const errorMsg = validateTableColumnSelection(visibleColumns)
        if (errorMsg) {
            setColumnErrorMsg(errorMsg)
            return
        }
        setColumnErrorMsg('')
        // setVisibleTableColumnKeys(visibleColumns.map(column => column.key))
        setTableInitPhase('init')
        setTableKey(prev => prev + 1)

        try {
            await saveUserPreferences(userId, visibleColumns as any, TABLE_SOURCE_LISTVIEW, tablePreferenceId)
            if (!tablePreferenceId) {
                const savedPreference = await getUserPreferences(userId, TABLE_SOURCE_LISTVIEW)
                setTablePreferenceId(savedPreference?.ID)
            }
        } catch (error) {
            console.error('Failed to save table column preferences:', error)
        }
    }, [tablePreferenceId, userId])

    const buildTable = (_tabKey: RequirementTabKey, linkPath = 'detail-view/ItemGUID') => {
        // Use KPI filtered items if active, otherwise use search/filter results
        const handleTableClickCapture = (e: MouseEvent<HTMLElement>) => {
            const btn = (e.target as HTMLElement).closest('button')
            if (!btn) return
            const isInsideConfigPopover = !!btn.closest('.column-settings')
            const isSaveBtn = btn.textContent?.trim().toLowerCase() === 'save' ||
                btn.querySelector('.rb-save') !== null
            if (isInsideConfigPopover && isSaveBtn) {
                isSaveClickRef.current = true
            }
        }

        const tableColumns = [
            ...GRANT_TABLE_COLUMNS,
            { key: 'actions', label: '', canSort: false }
        ]

        const tableData = exportData.map((row: any) => ({
            ...row,
            actions: (
                <RhyBusButton
                    ariaLabel="Edit"
                    leftIcon="icon-rb-pencil"
                    variant="text"
                    size="xs"
                    onClick={() => navigate(`/createcase/${row.ItemGUID ?? row.ItemGuid ?? ''}`)}
                />
            )
        }))

        return (
            <div onClickCapture={handleTableClickCapture}>
                <RhyBusTableView
                    key={tableKey}
                    groupRows={true}
                    groupSize={0}
                    groupGap={12}
                    rowGap={true}
                    colorCode={(value: string) => statusColorMap[value] ?? ''}
                    colorCodeColumn="status"
                    contentItems={exportData}
                    headerItems={GRANT_TABLE_COLUMNS}
                    copyTo={handleSelectionChange}
                    isAccordian={false}
                    isCheckbox={true}
                    isSorting={true}
                    isResize={true}
                    isColumnReordering={true}
                    isTableHeaders={true}
                    isColumnVisibility={true}
                    onSaveVisibleColumns={handleSaveVisibleColumns}
                    lastColumnWidth={235}
                    firstColumnWidth={125}
                    linkColumnKey="userCaseId"
                    linkPath={_tabKey === 'saved' ? 'createcase/ItemGUID' : linkPath}
                    size="middle"
                    stickyColumns="first-2-last-2"
                    stickyHeader={true}
                    className="list-view-table"
                    statusBgcolorClass="kpi-bg-color"
                    renderActionsWidth={40}
                    renderActions={(_row: any) => (
                        <RhyBusButton
                            ariaLabel="Edit"
                            leftIcon="icon-rb-pencil"
                            variant="text"
                            size="xs"
                            onClick={() => navigate(`/createcase/${_row.ItemGUID ?? _row.ItemGuid ?? ''}`)}
                        />
                    )}

                />
            </div>
        )
    }

    const tabs = [
        {
            tabtitle: 'All Cases',
            count: activeTab === 0 ? exportData.length : tabCounts.all,
            is_active: activeTab === 0,
            content: buildTable('all', 'detail-view/ItemGUID')
        },
        {
            tabtitle: 'Drafts',
            count: activeTab === 1 ? exportData.length : tabCounts.saved,
            is_active: activeTab === 1,
            content: buildTable('saved', 'createcase/ItemGUID')
        },
        {
            tabtitle: 'Canceled',
            count: activeTab === 2 ? exportData.length : tabCounts.canceled,
            is_active: activeTab === 2,
            content: buildTable('canceled')
        }
    ]

    const handleFilterChange = (_key: string, _value: { label: string, value: string }[], allFilters: FilterState) => {
        setFilters(allFilters)
        setKpiFilteredItems(null)
    }

    const handleClearAll = () => {
        setFilters(buildInitialFilters(dynamicFilterConfig))
        setSearchText('')
        setKpiFilteredItems(null)
        setSearchInputKey(prev => prev + 1)
    }

    /**
     * Handles KPI item click to filter table data
     * Applies the KPI filter predicate to show only items matching the clicked KPI
     */
    const handleItemClick = (item: any) => {
        try {
            const filtered = applyKpiFilter(filteredRequirements, item)
            setKpiFilteredItems(filtered)
        } catch (error) {
            console.error('Error in handleItemClick:', error)
        }
    }

    const handleHeaderClick = () => {
        setFilters(buildInitialFilters(dynamicFilterConfig))
        setSearchText('')
        setKpiFilteredItems(null)
        setSearchInputKey(prev => prev + 1)
        setFiltersKey(prev => prev + 1)
    }

    const handleSaveColumns = async (savedColumns: { available: ListViewExportColumn[], selected: ListViewExportColumn[] }) => {
        const selectedColumns = savedColumns.selected.map(column => ({ ...column, IsSelected: true }))
        const availableColumns = savedColumns.available.map(column => ({ ...column, IsSelected: false }))
        const allColumns = [...selectedColumns, ...availableColumns]

        setAvailableExportColumns(availableColumns)
        setSelectedExportColumns(selectedColumns)

        try {
            await saveUserPreferences(userId, allColumns, EXPORT_SOURCE_LISTVIEW, userPreferenceId)
            if (!userPreferenceId) {
                const savedPreference = await getUserPreferences(userId, EXPORT_SOURCE_LISTVIEW)
                setUserPreferenceId(savedPreference?.ID)
            }
        } catch (error) {
            console.error('Failed to save export column preferences:', error)
        }
    }

    const handleReset = async ({ isReset }: { isReset: boolean }) => {
        if (!isReset) {
            return
        }

        const configuredColumns = await getAvailableColumnsByReference(EXPORT_SOURCE_LISTVIEW)
        const fallbackColumns = buildDefaultExportColumns()
        const resetColumns = (configuredColumns.length > 0 ? configuredColumns : fallbackColumns).map(column => ({
            ...column,
            IsSelected: column.IsSelected !== false
        }))

        setSelectedExportColumns(resetColumns.filter(column => column.IsSelected !== false))
        setAvailableExportColumns(resetColumns.filter(column => column.IsSelected === false))

        try {
            await saveUserPreferences(userId, resetColumns, EXPORT_SOURCE_LISTVIEW, userPreferenceId)
        } catch (error) {
            console.error('Failed to reset export column preferences:', error)
        }
    }

    const onClickCopyTo = async (
        copiedData: any,
        singleSelect: boolean,
        bulkSelect: boolean,
        checkboxData?: { value: string, label: string }[]
    ) => {
        if (!hasSelectedRequirements(copiedData)) {
            setShowAlert(REQUIREMENT_SELECTION_VALIDATION_MESSAGE)
            return
        }

        if (copiedData.length > 1) {
            setShowAlert(COPY_VALIDATION_MESSAGE)
            return
        }

        try {
            const selectedItem = copiedData[0]

            navigate('/createcase', {
                state: {
                    prevPath: (location.state as { path?: string } | null)?.path || location.pathname,
                    isCopied: true,
                    copiedData: selectedItem,
                    singleSelect,
                    bulkSelect,
                    checkboxData: checkboxData ?? []
                }
            })
        } catch (error) {
            console.error('Error copying items:', error)
        }
    }

    const handleSearchInput = (event: FormEvent<HTMLInputElement>) => {
        setSearchText((event.target as HTMLInputElement).value)
    }

    const handleExportContainerClickCapture = (event: MouseEvent<any>) => {
        const button = (event.target as HTMLElement).closest('button')
        const buttonLabel = button?.getAttribute('aria-label')?.trim()

        if (buttonLabel !== 'Export to CSV') {
            return
        }

        if (!hasSelectedRequirements(checkedItems)) {
            event.preventDefault()
            event.stopPropagation()
            setShowAlert(REQUIREMENT_SELECTION_VALIDATION_MESSAGE)
        }
    }

    return (
        <main id="main-content">
            <RhyBusAlert
                size="md"
                show={!!showAlert}
                variant="confirm"
                message={showAlert}
                confirmLabel="OK"
                onConfirm={() => {
                    setCheckedItems([])
                    setTableKey(prev => prev + 1)
                    setShowAlert(false)
                }}
            />
            <RhyBusPageHeader pageTitle="Cases" pageHeaderIcon="icon-rb-requirements"
                headerRightContent={
                    <div className='d-flex align-items-center flex-wrap gap-2'>
                        <RhyBusInputText
                            key={searchInputKey}
                            type='search'
                            iconRight={true}
                            wrapperClassName='min-w-200 search'
                            customIcon="icon-rb-search"
                            placeholder="Search"
                            defaultValue={searchText}
                            onInput={handleSearchInput}
                        />
                        {activeTab === 0 && <RhyBusButton
                            ariaLabel={showSidebar ? 'Hide filters sidebar' : 'Show filters sidebar'}
                            className="list-view-filter-toggle"
                            leftIcon="icon-rb-filters"
                            onClick={() => setShowSidebar(current => !current)}
                            variant="primary"
                        />}
                    </div>
                }
            />
            <div className={`container list-view-layout ${isExpanddetail ? 'full-screen' : ''}`}>
                <div className='row'>
                    <div className="col-12 mt-0">
                        <RhyBusCard
                            className={isExpanddetail ? 'border-radius-0 rb-card-shadow-flat' : ''}
                            cardSize='lg'
                            cardbodyclassName='py-0'
                            cardBody={
                                isLoading
                                    ? <div className="d-flex justify-content-center py-4">
                                        <PageOverlay />
                                    </div>
                                    : <RhyBusTabs
                                        anchorclass="mb-0 font-18"
                                        className="list-view-tabs-shell"
                                        contentstyles="list-view-tabs-table"
                                        badge={true}
                                        badgeType='text'
                                        tabsize="xlarge"
                                        isButtons={
                                            <div className='d-flex gap-2 flex-wrap justify-content-end align-items-center listpage-buttons'>
                                                {activeTabKey === 'all' && exportData.length > 0 && (
                                                    <RhyBusCopyTo
                                                        label='Copy To'
                                                        buttonIcon='icon-rb-copy'
                                                        copiedData={copiedData}
                                                        onClickCopyTo={onClickCopyTo}
                                                        showPopup={false}
                                                        singleSelect={true}
                                                        bulkSelect={false}
                                                        popoverSize='md'
                                                    />
                                                )}
                                                {activeTabKey === 'all' && exportData.length > 0 && (
                                                    <div
                                                        ref={exportCsvContainerRef}
                                                        className='export-to-csv'
                                                        onClickCapture={handleExportContainerClickCapture}
                                                    >
                                                        <RhyBusExportToCSV
                                                            availableColumnsData={availableExportColumns}
                                                            selectedColumnsData={selectedExportColumns}
                                                            onSave={handleSaveColumns}
                                                            onReset={handleReset}
                                                            data={checkedItems}
                                                            exportColumns={selectedExportColumns}
                                                            file={getListViewExportFileName(activeTabKey)}
                                                            label="Export to CSV"
                                                            isUserPreference={true}
                                                            buttonSize=""
                                                            actionBtnSize=""
                                                            pill={true}
                                                            settingsStyle={{
                                                                '--rb-btn-bg': 'var(--rb-color-primary)',
                                                            } as any}
                                                        />
                                                    </div>
                                                )}
                                                <div className='divider vertical'></div>
                                                {activeTabKey === 'all' && (
                                                    <RhyBusButton
                                                        ariaLabel={isExpanddetail ? 'Collapse' : 'Expand'}
                                                        leftIcon={isExpanddetail ? 'icon-rb-arrow-angle-shrink me-0 bold font-15' : 'icon-rb-arrow-angle-expand me-0 bold font-15'}
                                                        variant="text"
                                                        size="xs"
                                                        onClick={expanddetail}
                                                    />
                                                )}
                                            </div>
                                        }
                                        items={tabs}
                                        setActiveTab={(tab: number) => {
                                            if (tab !== activeTab) {
                                                setFilters(buildInitialFilters(dynamicFilterConfig))
                                                setSearchText('')
                                                setKpiFilteredItems(null)
                                                setSearchInputKey(prev => prev + 1)
                                                setFiltersKey(prev => prev + 1)
                                            }
                                            setActiveTab(tab)
                                            if (tab !== 0) setShowSidebar(false)
                                        }}
                                        tablistclassName="overflow-auto"
                                    />
                            }
                        />
                        {showSidebar && <>
                            <div className="filters-section list-view-sidebar-overlay">
                                <RhyBusFilters
                                    key={filtersKey}
                                    ref={RhyBusFiltersRef}
                                    buttonSize="sm"
                                    change={filters}
                                    buttonVariant="secondary"
                                    clearAllIcon="icon-rb-arrow-rotate-clockwise"
                                    closeVariant="text"
                                    config={cascadingFilterConfig}
                                    isCloseButton={true}
                                    isSearch={false}
                                    label="Filters"
                                    onClearAll={handleClearAll}
                                    onClose={() => setShowSidebar(false)}
                                    onFilterChange={handleFilterChange}
                                    onSearch={setSearchText}
                                    page="list"
                                    searchWrapperClassName="col-12"
                                    size="md"
                                    wrapperClassName="col-12 col-lg-6"
                                    filterIcon='icon-rb-filters'
                                />
                                <RhyBusKPI
                                    groups={kpiGroups}
                                    header={{
                                        title: 'Total Cases',
                                        count: items.filter((item: any) => item.tabKey !== 'saved').length
                                    }}
                                    onHeaderClick={handleHeaderClick}
                                    onItemClick={handleItemClick}
                                    countClassName='kpi-bg-color'
                                />
                            </div>
                        </>}
                    </div>
                </div>
            </div>
        </main>
    )
}
